/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, Fragment } from 'react';
import DateRangePicker from 'react-dates/lib/components/DateRangePicker';
import isWithinRange from 'date-fns/is_within_range';
import isSameDay from 'date-fns/is_same_day';
import addDays from 'date-fns/add_days';
import format from 'date-fns/format';
import differenceInCalendarDays from 'date-fns/difference_in_calendar_days';
import 'react-dates/initialize';
import ReactSVG from 'react-svg';
import moment from 'moment';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import customArrow from 'custom_datepicker_arrow.svg';
import useWindowSize from '../../hooks/useWindowSize';
import Ratings from '../Ratings';
import DatepickerArrow from '../DatepickerArrow';
import BookingFormConfirmation from './BookingFormConfirmation';
import getAverage from '../../utils/getAverage';
import checkIsCalendarDayBlocked from '../../utils/checkIsCalendarDayBlocked';
import { queryDateFormat } from '../../constants/formats.json';
import ErrorBoundary from '../ErrorBoundary';
import { ApolloProvider, useQuery } from 'react-apollo';
import { useLazyQuery } from '@apollo/react-hooks';
import { QUOTES } from '../../constants/queries';
import { client } from '../client';
import { QuoteResponse } from './QuoteResponse';
import QuoteError from './QuoteError';

function BookingForm({
  property,
  reviews,
  rating,
  show_ivol,
  property_bookings,
  current_user,
}) {
  const { width, height } = useWindowSize();

  const bookingFormRef = useRef(null);
  const bookingFormHeaderRef = useRef(null);
  const bookingFormFooterRef = useRef(null);
  const bookingFormWrapperRef = useRef(null);

  const [price, setPrice] = useState(property?.nightly_rate);
  const [localStartDate, setLocalStartDate] = useState(null);
  const [localEndDate, setLocalEndDate] = useState(null);
  const [focusedState, setFocusedState] = useState(null);
  const [guests, setGuests] = useState(0);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [total, setTotal] = useState(null);
  const [totalNights, setTotalNights] = useState(null);
  const [perNightTotal, setPerNightTotal] = useState(0);
  const [optionalFees, setOptionalFees] = useState([]);
  const [mandatoryFees, setMandatoryFees] = useState([]);
  const [useNightlyRate, setUseNightlyRate] = useState(false);

  // error states
  const [guestsError, setGuestsError] = useState(false);
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [minNightsError, setMinNightsError] = useState(false);

  const [minNights, setMinNights] = useState(null);

  const [quoteSearch, { data: quoteResponse, loading: quoteLoading }] = useLazyQuery(QUOTES);
  const [quoteAvailable, setQuoteAvailable] = useState(false);

  const storeData = ignoreGuests => {
    // !!! IMPORTANT !!! If adding any booking form data to localStorage, be sure to remove the data after 1 week
    // (See clear_booking_form_data.js)
    if (totalNights) window.localStorage.setItem('totalNights', totalNights);
    if (localStartDate) window.localStorage.setItem('checkIn', localStartDate);
    if (localEndDate) window.localStorage.setItem('checkOut', localEndDate);
    if (guests && !ignoreGuests) window.localStorage.setItem('guests', guests);
    if (firstName) window.localStorage.setItem('firstName', firstName);
    if (lastName) window.localStorage.setItem('lastName', lastName);
    if (email) window.localStorage.setItem('email', email);
    if (phone) window.localStorage.setItem('phone', phone);
    if (message) window.localStorage.setItem('message', message);
    const destroy_booking_form_data_date = window.localStorage.getItem(
      'destroy_booking_form_data'
    );
    if (!destroy_booking_form_data_date)
      window.localStorage.setItem('destroy_booking_form_data', new Date());
  };

  // Listen for data changes
  useEffect(() => {
    storeData();
  }, [
    totalNights,
    localStartDate,
    localEndDate,
    guests,
    firstName,
    lastName,
    email,
    phone,
    message,
  ]);

  // Persist booking form values
  useEffect(() => {
    // !!! IMPORTANT !!! If adding any booking form data to localStorage, be sure to remove the data after 1 week
    // (See clear_booking_form_data.js)

    const queries = window.location.search.substr(1).split('&');
    const queryCheckIn = (
      queries.find(q => q.includes('check_in')) || ''
    ).split('=')[1];
    const queryCheckOut = (
      queries.find(q => q.includes('check_out')) || ''
    ).split('=')[1];
    const queryGuests = (queries.find(q => q.includes('guests')) || '').split(
      '='
    )[1];

    // Get Local Storage Values
    const lsTotalNights = window.localStorage.getItem('totalNights');
    const lsCheckIn = window.localStorage.getItem('checkIn');
    const lsCheckOut = window.localStorage.getItem('checkOut');
    const lsGuests = window.localStorage.getItem('guests');
    const lsFirstName = window.localStorage.getItem('firstName');
    const lsLastName = window.localStorage.getItem('lastName');
    const lsEmail = window.localStorage.getItem('email');
    const lsPhone = window.localStorage.getItem('phone');
    const lsMessage = window.localStorage.getItem('message');

    // Check if one of the dates is invalid, if so, don't populate form
    const datesInvalid = property_bookings.some(
      ({ table }) =>
        moment(lsCheckIn).isBetween(table.start_date, table.end_date) ||
        moment(lsCheckOut).isBetween(table.start_date, table.end_date) ||
        moment(queryCheckIn).isBetween(table.start_date, table.end_date) ||
        moment(queryCheckOut).isBetween(table.start_date, table.end_date) ||
        moment(lsCheckIn).isSame(table.start_date) ||
        moment(lsCheckOut).isSame(table.start_date) ||
        moment(queryCheckIn).isSame(table.start_date) ||
        moment(queryCheckOut).isSame(table.start_date) ||
        moment(queryCheckIn).isBefore(moment()) ||
        moment(lsCheckIn).isBefore(moment())
    );
    const datesValid = !datesInvalid;

    if (datesValid) {
      if (lsTotalNights) setTotalNights(parseInt(lsTotalNights));
      if (lsFirstName) setFirstName(lsFirstName);
      if (lsLastName) setLastName(lsLastName);
      if (lsEmail) setEmail(lsEmail);
      if (lsPhone) setPhone(lsPhone);
      if (lsMessage) setMessage(lsMessage);

      // Set Rails Form Values
      if (lsFirstName)
        document.querySelector('#inquiry_first_name').value = lsFirstName;
      if (lsLastName)
        document.querySelector('#inquiry_last_name').value = lsLastName;
      if (lsEmail) document.querySelector('#inquiry_email').value = lsEmail;
      if (lsPhone) document.querySelector('#inquiry_phone').value = lsPhone;
      if (lsMessage)
        document.querySelector('#inquiry_message').value = lsMessage;
      if (lsCheckIn)
        document.querySelector('#inquiry_start_date').value = moment(
          lsCheckIn
        ).format('YYYY-MM-DD');
      if (lsCheckOut)
        document.querySelector('#inquiry_end_date').value = moment(
          lsCheckOut
        ).format('YYYY-MM-DD');

      if (queryCheckIn) {
        setLocalStartDate(moment(queryCheckIn, queryDateFormat));
      } else if (lsCheckIn) setLocalStartDate(moment(lsCheckIn));
      if (queryCheckOut) {
        setLocalEndDate(moment(queryCheckOut, queryDateFormat));
      } else if (lsCheckOut) setLocalEndDate(moment(lsCheckOut));
      if (queryGuests) {
        setGuests(parseInt(queryGuests.split('=')[1]));
      } else if (lsGuests) setGuests(parseInt(lsGuests));
    }
  }, []);

  useEffect(() => {
    if (currentStep === 2) {
      // Set Rails Form Values
      if (guests) document.querySelector('#inquiry_guests').value = guests;
      if (firstName)
        document.querySelector('#inquiry_first_name').value = firstName;
      if (lastName)
        document.querySelector('#inquiry_last_name').value = lastName;
      if (email) document.querySelector('#inquiry_email').value = email;
      if (phone) document.querySelector('#inquiry_phone').value = phone;
      if (message) document.querySelector('#inquiry_message').value = message;
      if (localStartDate)
        document.querySelector('#inquiry_start_date').value = moment(
          localStartDate
        ).format('YYYY-MM-DD');
      if (localEndDate)
        document.querySelector('#inquiry_end_date').value = moment(
          localEndDate
        ).format('YYYY-MM-DD');
    }
  }, [currentStep]);

  const setTotalValue = () => {
    if (total)
      document.querySelector('#inquiry_total').value = total.toFixed(2);
  };

  const setPerNightTotalValue = () => {
    if (perNightTotal)
      document.querySelector(
        '#inquiry_calculated_nightly_rate'
      ).value = Math.ceil(
        perNightTotal /
          (totalNights % 7 === 0 ? totalNights / totalNights : totalNights)
      ).toFixed(2);
  };

  const handleRequest = () => {
    if (currentStep === 1) {
      if (!guests) {
        setGuestsError(true);
      } else {
        setCurrentStep(2);
        disableBodyScroll(
          document.querySelector('.booking-form-input-container')
        );
      }
      // setTotalValue();
      // setPerNightTotalValue();
    } else if (currentStep === 2) {
      if (!guests) setGuestsError(true);
      else if (guests) setGuestsError(false);

      if (!firstName) setFirstNameError(true);
      else if (firstName) setFirstNameError(false);

      if (!lastName) setLastNameError(true);
      else if (lastName) setLastNameError(false);

      if (!email) setEmailError(true);
      else if (email) setEmailError(false);

      storeData(true);

      if (guests && firstName && lastName && email && !minNightsError) {
        setTotalValue();
        setPerNightTotalValue();
        // Set errors false
        setFirstNameError(false);
        setLastNameError(false);
        setEmailError(false);
        // Submit booking form
        document.querySelector('#inquiry_submit').click();
        // Show form confirmation
        setCurrentStep(3);
        // Reset form values
        // setLocalStartDate(null);
        // setLocalEndDate(null);
        // setGuests(1);
        // setFirstName('');
        // setLastName('');
        // setEmail('');
        document.querySelector('#new_inquiry').reset();
      }
    }
  };

  // Set datepicker inputs to readonly to hide mobile keyboard
  useEffect(() => {
    document.querySelectorAll('input.DateInput_input').forEach(elem => {
      elem.setAttribute('readonly', 'readonly');
    });
  }, []);

  useEffect(() => {
    const bookingForm = document.querySelector('.property-booking-form');
    // !!! IMPORTANT !!!
    // Keeps Booking form in position when changing to second step
    if (currentStep !== 1) {
      bookingForm.classList.add('mobile-booking-form-full');
      bookingForm.classList.remove('mobile-booking-form-stuck');
    } else {
      bookingForm.classList.remove('mobile-booking-form-full');
      bookingForm.classList.add('mobile-booking-form-stuck');
    }
    // !!! IMPORTANT  !!!
  }, [currentStep]);

  // Set guests value on load
  useEffect(() => {
    document.querySelector('#inquiry_guests').value = 1;
  }, []);

  // Scrolling event listener
  useEffect(() => {
    const headerHeight = document.querySelector('.header-nav').offsetHeight;
    const bookingForm = document.querySelector('.property-booking-form');
    const contentContainer = document.querySelector('.main-content-container');
    const contentWrapper = document.querySelector('.main-content-wrapper');
    const pxFromTop = 520; // For desktop, the px from top will always be 520. Not showing navbar for mobile

    const handleScroll = () => {
      // !!! DO NOT TOUCH !!!
      // Important for booking for being stuck "inside" container
      if (window.scrollY + headerHeight >= pxFromTop) {
        // position fixed
        bookingForm.classList.remove('booking-form-bottom');
        bookingForm.classList.add('booking-form-sticky');
        contentContainer.style.paddingRight = `${350 + 50}px`;
      } else {
        bookingForm.classList.remove('booking-form-sticky');
        contentContainer.style.paddingRight = '50px';
      }
      if (
        bookingForm &&
        contentWrapper &&
        contentWrapper.getBoundingClientRect().bottom - 70 <
          bookingForm.getBoundingClientRect().bottom
      ) {
        // stick to bottom
        bookingForm.classList.remove('booking-form-sticky');
        bookingForm.classList.add('booking-form-bottom');
      }
      // !!! DO NOT TOUCH !!!
    };

    // Set modal class on page load
    bookingForm.classList.add('mobile-booking-form-stuck');

    document.addEventListener('scroll', handleScroll);

    return () => document.removeEventListener('scroll', handleScroll);
  }, []);

  // Window resize listener (for booking form fixed right positioning)
  useEffect(() => {
    const bookingForm = document.querySelector('.property-booking-form');

    const handleResize = () => {
      if (width > 1400) {
        const contentWrapper = document.querySelector('.main-content-wrapper');
        if (contentWrapper) {
          bookingForm.style.right = `${contentWrapper.offsetLeft + 38}px`;
        }
      } else if (bookingForm) {
        bookingForm.style.right = `${document.body.scrollWidth -
          (bookingForm.getBoundingClientRect().left +
            bookingForm.offsetWidth)}px`;
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [width]);

  // Change booking form size when step changes
  useEffect(() => {
    if (width > 768) {
      // if (currentStep === 2) {
      //   bookingFormRef.current.style.height = '100%';
      // } else {
      //   bookingFormRef.current.style.height = 'auto';
      // }

      if (
        bookingFormWrapperRef.current &&
        bookingFormFooterRef.current &&
        bookingFormHeaderRef.current &&
        bookingFormRef.current
      ) {
        const bookingFormHeight = bookingFormRef.current.clientHeight;
        const headerHeight = bookingFormHeaderRef.current.clientHeight;
        const footerHeight = bookingFormFooterRef.current.clientHeight;
        const bookingFormPaddingTop = parseInt(
          window.getComputedStyle(bookingFormRef.current).paddingTop
        );
        const bookingFormPaddingBottom = parseInt(
          window.getComputedStyle(bookingFormRef.current).paddingBottom
        );

        bookingFormWrapperRef.current.style.height = `${bookingFormHeight -
          headerHeight -
          footerHeight -
          bookingFormPaddingTop -
          bookingFormPaddingBottom}px`;
      }
    }
  }, [currentStep, width, height]);

  useEffect(() => {
    if (localStartDate && localEndDate) {
      quoteSearch({
        variables: {
          propertyId: property?.id,
          checkIn: moment(localStartDate).format('YYYYMMDD'),
          checkOut: moment(localEndDate).format('YYYYMMDD'),
        },
      });
    }
  }, [localStartDate, localEndDate])

  useEffect(() => {
    if (!quoteResponse) return;

    const {
      quote: {
        result: {
          quote_available: quoteAvailable,
          rate,
          rental_amount: rentalAmount,
        },
      },
    } = quoteResponse;

    const isValid = quoteAvailable || rate > 0;

    document.querySelector('#inquiry_total').value = rentalAmount;
    document.querySelector('#inquiry_calculated_nightly_rate').value = rate;

    setQuoteAvailable(isValid);
    setTotal(rentalAmount);
    setPerNightTotal(rate);
  }, [quoteResponse])

  useEffect(() => {
    if (width <= 768 && currentStep !== 1) {
      disableBodyScroll(
        document.querySelector('.booking-form-input-container')
      );
    } else {
      clearAllBodyScrollLocks();
      document.querySelector('body').style.overflow = '';
    }
  }, [currentStep, width]);

  // Set property fees
  useEffect(() => {
    setOptionalFees(property.fees.filter(fee => fee.table.is_optional));
    setMandatoryFees(property.fees.filter(fee => !fee.table.is_optional));
  }, [property]);

  let modalWarning = 'This does not guarantee you a booking.';
  if (currentStep === 2) {
    if (total) {
      // No Missing rates
      modalWarning =
        'The quote above is an estimate. This does not guarantee you a booking.';
    } else if (localStartDate && localEndDate) {
      // Some Missing rates
      modalWarning =
        'Oops! Some of these dates are missing rates, so the price above is an average for this rental. You may still inquire, but this does not guarantee you a booking';
    } else {
      // Some dates selected
      modalWarning =
        'No dates are selected, so the price above is an average for this rental. You may still inquire, but this does not guarantee you a booking.';
    }
  }

  const calcFormHeight = () => {
    const formWrapper = document.getElementById('booking-form-wrapper');
    if (currentStep !== 2) {
      return 'auto';
    }
    if (width <= 768) {
      return '100%';
    }
    if (height >= 1200) {
      formWrapper.style.height = 'auto';
      return 'auto';
    }
    return 'calc(100vh - 206px)';
  };

  const isWeek = totalNights % 7 === 0;
  const weeksLength = totalNights / 7;

  return (
    <aside
      ref={bookingFormRef}
      className="property-booking-form"
      style={{ height: calcFormHeight() }}
    >
      {/* Mobile Header */}
      <header className="booking-form-title">
        <h2>Request to Book</h2>
        <button
          aria-label="Cancel Request to Book"
          name="Cancel Request to Book"
          type="button"
          className="underline text-secondary"
          onClick={() => {
            setCurrentStep(1);
            clearAllBodyScrollLocks();
            document.querySelector('body').style.overflow = '';
          }}
        >
          Cancel
        </button>
      </header>

      {/* Desktop Header */}
      <header
        ref={bookingFormHeaderRef}
        className="booking-form-header"
        style={{
          minHeight:
            total && currentStep === 2 && localStartDate && localEndDate
              ? `calc(51px + ${17 * property.fees.length}px)`
              : '',
        }}
      >
        <section
          className={`booking-form-header-total ${
            currentStep >= 2 ? 'space-between' : ''
          } ${show_ivol ? 'show_ivol' : ''}`}
        >
          <div className='header'>
            {quoteAvailable && (price || total) ? (
              <h2>
                  {total
                    ? `$${parseFloat(total).toFixed(2)} approx. total`
                    : `Starting from $${price}/night`}
                </h2>
              ) : (
                <span>Inquire for Rates</span>
              )}
            <Ratings rating={rating} review_count={reviews.length} />
          </div>
          <div className="booking-form-price-container">
          {quoteResponse && quoteAvailable && !show_ivol && (
            <QuoteResponse
              quoteResponse={quoteResponse}
              quoteLoading={quoteLoading}
              currentStep={currentStep}
            />
          )}
          {quoteResponse && !quoteAvailable && !show_ivol && (
            <QuoteError quoteResponse={quoteResponse} />
          )}

          </div>
        </section>

        {currentStep === 1 && (
          <section
            className={`mobile-button-header-wrapper ${
              show_ivol ? 'ivol' : ''
            }`}
          >
            {show_ivol && (
              <button
                aria-label="Book Now"
                name="Book Now"
                type="button"
                className="btn btn--dark property_ivol ivol-btn mobile"
                onClick={() => {
                  // window.pageYOffset works on ios Safari
                  const scrollTopVal = Math.max(
                    window.pageYOffset,
                    document.querySelector('html').scrollTop
                  );
                  document
                    .querySelector('body')
                    .setAttribute('data-scroll', scrollTopVal);
                  document
                    .querySelector('#ivol-modal')
                    .classList.remove('hidden');
                  document.querySelector('body').style.overflow = 'hidden';
                  document.querySelector('body').style.position = 'fixed';
                }}
              >
                Book Now
              </button>
            )}
            <button
              name={show_ivol ? 'Send Inquiry' : 'Request To Book'}
              type="button"
              className={`btn btn--blue-grad property_booking_request mobile ${
                show_ivol ? 'ivol-btn' : ''
              }`}
              onClick={() => setCurrentStep(2)}
            >
              {show_ivol ? 'Send Inquiry' : 'Request To Book'}
            </button>
          </section>
        )}
      </header>

      {currentStep !== 3 ? (
        <Fragment>
          <div
            ref={bookingFormWrapperRef}
            className={`booking-form-wrapper ${
              focusedState ? 'date-picker-focused' : ''
            }`}
            id="booking-form-wrapper"
          >
            <section className="booking-form-input-container">
              <div
                className={`input custom-connected-date-picker custom-calendar-wrapper ${
                  minNightsError ? 'date-picker-error' : ''
                }`}
              >
                {/* eslint-disable-next-line */}
                <label htmlFor="property_booking_start_date">Dates</label>
                <DateRangePicker
                  startDate={localStartDate}
                  startDateId="property_booking_start_date"
                  startDatePlaceholderText="Check In"
                  endDate={localEndDate}
                  endDateId="property_booking_end_date"
                  endDatePlaceholderText="Check Out"
                  onDatesChange={({ startDate, endDate }) => {
                    if (startDate) {
                      setLocalStartDate(startDate);
                      setLocalEndDate(null);
                      document.querySelector(
                        '#inquiry_start_date'
                      ).value = startDate.format('YYYY-MM-DD');
                    }
                    if (endDate) {
                      setLocalEndDate(endDate);
                      document.querySelector(
                        '#inquiry_end_date'
                      ).value = endDate.format('YYYY-MM-DD');
                    }
                  }}
                  focusedInput={focusedState}
                  onFocusChange={e => setFocusedState(e)}
                  numberOfMonths={1}
                  minimumNights={0}
                  noBorder
                  navNext={<DatepickerArrow direction="next" />}
                  navPrev={<DatepickerArrow direction="prev" />}
                  customArrowIcon={<ReactSVG src={customArrow} />}
                  isDayBlocked={day => {
                    const nextBookingRate = property_bookings.find(booking =>
                      moment(booking.table.start_date).isAfter(localStartDate)
                    );
                    const isOutsideRange =
                      nextBookingRate &&
                      day.isAfter(moment(nextBookingRate.table.start_date));
                    const isSameDate =
                      nextBookingRate &&
                      day.isSame(nextBookingRate.table.start_date, 'day');
                    if (isOutsideRange && !localEndDate && !isSameDate) {
                      return true;
                    }
                    return checkIsCalendarDayBlocked({
                      day,
                      bookings: property_bookings,
                    });
                  }}
                />
                {minNightsError && (
                  <span className="error">
                    This listing has a {minNights} night booking minimum
                  </span>
                )}
              </div>
              <div
                className={`input ${guestsError ? 'field_with_errors' : ''}`}
              >
                {/* eslint-disable-next-line */}
                <label htmlFor="property_booking_guests">*Guests</label>
                <select
                  id="property_booking_guests"
                  aria-label="guests"
                  name="guests"
                  value={guests}
                  onChange={e => {
                    if (parseInt(e.target.value)) setGuestsError(false);
                    setGuests(parseInt(e.target.value));
                    document.querySelector('#inquiry_guests').value =
                      e.target.value;
                  }}
                >
                  <option value={0}>Select Guests</option>
                  {/* eslint-disable-next-line */}
                  {Array.apply(null, { length: property.sleeps }).map(
                    (_, ind) => (
                      <option key={ind} value={ind + 1}>
                        {ind + 1} Guest{ind + 1 > 1 && 's'}
                      </option>
                    )
                  )}
                </select>
                {guestsError && (
                  <span className="error">Guests can't be blank</span>
                )}
              </div>

              {currentStep === 2 && (
                <Fragment>
                  <div className="input-row property-booking-form-input-row">
                    <div
                      className={`input ${
                        firstNameError ? 'field_with_errors' : ''
                      }`}
                    >
                      {/* eslint-disable-next-line */}
                      <label htmlFor="property_booking_first_name">
                        *First Name
                      </label>
                      <input
                        type="text"
                        aria-label="first_name"
                        name="first_name"
                        placeholder="First"
                        id="property_booking_first_name"
                        value={firstName}
                        onChange={e => {
                          setFirstName(e.target.value);
                          document.querySelector('#inquiry_first_name').value =
                            e.target.value;
                        }}
                      />
                      {firstNameError && (
                        <span className="error">First name can't be blank</span>
                      )}
                    </div>

                    <div
                      className={`input ${
                        lastNameError ? 'field_with_errors' : ''
                      }`}
                    >
                      {/* eslint-disable-next-line */}
                      <label htmlFor="property_booking_last_name">
                        *Last Name
                      </label>
                      <input
                        type="text"
                        aria-label="last_name"
                        name="last_name"
                        placeholder="Last"
                        id="property_booking_last_name"
                        value={lastName}
                        onChange={e => {
                          setLastName(e.target.value);
                          document.querySelector('#inquiry_last_name').value =
                            e.target.value;
                        }}
                      />
                      {lastNameError && (
                        <span className="error">Last name can't be blank</span>
                      )}
                    </div>
                  </div>

                  <input
                    type="hidden"
                    name="type"
                    value={'PropertyInquiry'}
                  />

                  <div
                    className={`input ${emailError ? 'field_with_errors' : ''}`}
                  >
                    {/* eslint-disable-next-line */}
                    <label htmlFor="property_booking_email">*Email</label>
                    <input
                      type="email"
                      aria-label="email"
                      name="email"
                      placeholder="Email Address"
                      id="property_booking_email"
                      value={email}
                      onChange={e => {
                        setEmail(e.target.value);
                        document.querySelector('#inquiry_email').value =
                          e.target.value;
                      }}
                    />
                    {emailError && (
                      <span className="error">Email can't be blank</span>
                    )}
                  </div>

                  <div className="input">
                    {/* eslint-disable-next-line */}
                    <label htmlFor="property_booking_phone">Phone</label>
                    <input
                      type="tel"
                      aria-label="phone"
                      name="phone"
                      placeholder="Phone Number"
                      id="property_booking_phone"
                      value={phone}
                      onChange={e => {
                        setPhone(e.target.value);
                        document.querySelector('#inquiry_phone').value =
                          e.target.value;
                      }}
                    />
                  </div>

                  <div className="input">
                    {/* eslint-disable-next-line */}
                    <label htmlFor="property_booking_message">Message</label>
                    <textarea
                      aria-label="message"
                      name="message"
                      placeholder="Message"
                      id="property_booking_message"
                      value={message}
                      onChange={e => {
                        setMessage(e.target.value);
                        document.querySelector('#inquiry_message').value =
                          e.target.value;
                      }}
                    />
                  </div>
                </Fragment>
              )}

              <button
                name={currentStep === 2 ? 'Submit Request' : 'Request to Book'}
                type="button"
                className="btn btn--blue-grad property_booking_request"
                onClick={handleRequest}
                id={
                  currentStep === 2
                    ? 'booking-form-submit'
                    : 'booking-form-request'
                }
              >
                {currentStep === 2 ? 'Submit Request' : 'Request to Book'}
              </button>
              {show_ivol && (
                <button
                  aria-label="Book Now"
                  name="Book Now"
                  type="button"
                  className="btn btn--dark property_ivol"
                  onClick={() => {
                    document
                      .querySelector('#ivol-modal')
                      .classList.remove('hidden');
                    document.querySelector('body').style.overflow = 'hidden';
                    // disableBodyScroll(document.querySelector('.ivol-wrapper'));
                  }}
                >
                  Book Now
                </button>
              )}
              <p>{modalWarning}</p>
            </section>
            {/* {currentStep === 2 && <div className="input-gradient-overlay" />} */}
          </div>
        </Fragment>
      ) : (
        <BookingFormConfirmation
          email={email}
          setCurrentStep={setCurrentStep}
          current_user={current_user}
        />
      )}
    </aside>
  );
}

export default function BookingFormWrapper({ ...props }) {
  return (
    <ApolloProvider client={client}>
      <ErrorBoundary>
        <BookingForm {...props} />
      </ErrorBoundary>
    </ApolloProvider>
  );
}
